import { getAttributionPayload } from "../../analytics/attribution";
import { CheckoutRequest, saveQuizToAccount, startCheckout } from "../../features/start/api";
import { hasPlanAnswers, loadAnswers } from "../../features/start/answers";
import { getCurrentUser, postRequest } from "../../services/apiService";
import { Role, stringToRole } from "../types/User";

export type BillingCycle = "monthly" | "annual";
export type TierId = "bronze" | "silver" | "gold";

const CHECKOUT_INTENT_KEY = "checkoutIntent";
/** Long enough to open a sign-in email, short enough that a disclosure can't go stale. */
const LINK_INTENT_TTL_MS = 30 * 60 * 1000;

export interface CheckoutIntent {
  tier: TierId;
  cycle: BillingCycle;
  /** Set from the /start paywall: the words they agreed to, and where to come back. */
  start?: CheckoutRequest;
  savedAt?: number;
}

const ROLE_RANK: Role[] = [Role.USER, Role.BRONZE, Role.SILVER, Role.GOLD, Role.ADMIN];

function rankOf(r: Role | string | null | undefined): number {
  if (!r) return -1;
  const role = typeof r === "string" ? stringToRole(r) : r;
  if (!role) return -1;
  return ROLE_RANK.indexOf(role);
}

/** Remember which tier the visitor picked before sign-in. */
export function stashCheckoutIntent(
  tier: TierId,
  cycle: BillingCycle,
  start?: CheckoutRequest,
): void {
  const intent: CheckoutIntent = { tier, cycle, start, savedAt: Date.now() };
  sessionStorage.setItem(CHECKOUT_INTENT_KEY, JSON.stringify(intent));
  sessionStorage.removeItem("returnTo");
  // A sign-in link opens in a new tab, which has its own sessionStorage.
  if (start) {
    try {
      localStorage.setItem(CHECKOUT_INTENT_KEY, JSON.stringify(intent));
    } catch {
      /* the same tab still resumes */
    }
  }
}

function consumeCheckoutIntent(): CheckoutIntent | null {
  try {
    const session = sessionStorage.getItem(CHECKOUT_INTENT_KEY);
    const shared = localStorage.getItem(CHECKOUT_INTENT_KEY);
    sessionStorage.removeItem(CHECKOUT_INTENT_KEY);
    localStorage.removeItem(CHECKOUT_INTENT_KEY);
    const parsed = JSON.parse(session || shared || "null") as CheckoutIntent | null;
    if (!parsed?.tier || !parsed?.cycle) return null;
    if (!session && Date.now() - (parsed.savedAt || 0) > LINK_INTENT_TTL_MS) return null;
    return parsed;
  } catch {
    sessionStorage.removeItem(CHECKOUT_INTENT_KEY);
    return null;
  }
}

const giveUpAfter = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** Resumes a /start purchase after sign-in, with the answers on the account first. */
async function resumeStartCheckout(request: CheckoutRequest): Promise<void> {
  const answers = loadAnswers();
  if (hasPlanAnswers(answers) && answers.ageConfirmed) {
    // Never hold someone at the door of checkout over a profile write.
    await Promise.race([saveQuizToAccount(answers), giveUpAfter(2000)]);
  }
  const result = await startCheckout(request);
  if (result.kind === "redirect") {
    window.location.href = result.url;
    return;
  }
  // Back to the plan, which rereads whether this account gets a trial and restates the terms.
  window.location.href =
    result.kind === "trial_unavailable" ? "/start/plan?trial=used" : "/start/plan?checkout=failed";
}

/**
 * After sign-in, send the user straight to Stripe for the tier they already
 * picked on pricing (or the customer portal if they already subscribe).
 */
export async function resumeCheckoutAfterLogin(): Promise<boolean> {
  const intent = consumeCheckoutIntent();
  if (!intent) return false;

  let userRank = -1;
  try {
    const resp = await getCurrentUser();
    if (resp?.status === 200 && resp?.data) {
      userRank = rankOf(resp.data.role);
    }
  } catch {
    /* unauthenticated state should not happen here */
  }

  if (userRank > 0) {
    try {
      const resp = await postRequest("/api/v1/stripe/create-customer-portal-session", {});
      if (resp?.checkoutSessionUrl) {
        window.location.href = resp.checkoutSessionUrl;
        return true;
      }
    } catch (e) {
      console.error("[checkoutIntent] portal error", e);
    }
    window.location.href = `/pricing?tier=${intent.tier}&cycle=${intent.cycle}`;
    return true;
  }

  if (intent.start) {
    await resumeStartCheckout(intent.start);
    return true;
  }

  try {
    const resp = await postRequest("/api/v1/stripe/create-checkout-session", {
      tier: intent.tier,
      billingCycle: intent.cycle,
      attribution: getAttributionPayload() ?? undefined,
    });
    if (resp?.checkoutSessionUrl) {
      window.location.href = resp.checkoutSessionUrl;
      return true;
    }
  } catch (e) {
    console.error("[checkoutIntent] checkout error", e);
  }

  window.location.href = `/pricing?tier=${intent.tier}&cycle=${intent.cycle}`;
  return true;
}
